import React, { ReactElement, useEffect, useState } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { Box, Radio, Stack, Typography } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import { connect } from 'react-redux';
import { BaseAppActions } from 'state/baseapplication/Action';
import { LoginSchema } from 'validators/AuthenticationShema';
import { AuthActions } from 'state/authentication/Action';
import jwt_decode from 'jwt-decode';

import {
  AppFormikTextField,
  AppFormikPasswordField,
  AppPrimaryButton,
} from 'shared/components/AppComponents';
import { Utils } from 'shared/helpers';
import { AuthenticationService } from 'services/AuthenticationService';
import { EMP_ROLES } from 'state/authentication/Reducer';
// import { SettingService } from 'services/SettingService';
import OtpInput from 'react-otp-input';
import AzureLoginForm from './AzureLoginForm';

const LoginForm = (props: any): ReactElement => {
  const {
    setLoginData,
    doLogout,
  }: {
    setSocketHandshake: (data: string) => void;
    setLoginData: (data: any) => void;
    doLogout: () => void;
    setDefaultTheme: () => void;
    resetFieldLoggingOut: () => void;
  } = props;
  const [enabledMethods, setEnabledMethods] = useState<any>(null);
  const [verifyMethods, setVerifyMethods] = useState<any>(null);
  const [submittingMethod, setSubmittingMethod] = useState<any>(false);
  const [isMethodSelected, setIsMethodSelected] = useState<any>(null);
  const [otp, setOTP] = useState<any>(null);
  const [verifyingMethod, setVerifyingMethod] = useState<any>(null);

  const navigate = useNavigate();

  const loginForm = {
    email: '',
    password: '',
  };

  // API Calls
  const loginPortal = (formValues: any, callback: any): void => {
    AuthenticationService.login({ ...formValues })
      .then((response: any) => {
        callback(true, response);
      })
      .catch((error: any) => {
        callback(false);
        Utils.handleErrorResponse(error);
      });
  };

  const handleSendOTP = (): void => {
    setSubmittingMethod(true);
    AuthenticationService.sendOTP({
      email: enabledMethods?.email,
    })
      .then((res) => {
        setOTP(res?.data?.data?.code);
        setIsMethodSelected('email');
        setSubmittingMethod(false);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        setSubmittingMethod(false);
      });
  };

  const actionOnFormSubmit = (
    formValues: any,
    formik: FormikHelpers<any>
  ): void => {
    if (formik) {
      loginPortal({ ...formValues }, (status: any, response: any) => {
        formik.setSubmitting(false);
        if (status) {
          const { data } = response.data;
          const { accessToken, twoFactor } = data;
          if (twoFactor?.isEnabled) {
            setEnabledMethods({
              methods: twoFactor?.enabledMethods,
              email: formValues.email,
            });
            setLoginData({
              twoFactor,
            });
          } else {
            const decoded: any = jwt_decode(accessToken);
            setLoginData({
              accessToken,
              isUserLoggedIn: true,
              isOrgSelected: false,
              ...decoded,
              role: EMP_ROLES.Admin,
              twoFactor,
            });
            navigate('/orgs', { replace: true });
          }
        }
      });
    }
  };

  const handleTwoFALogin = (otp: any, type: any): void => {
    setVerifyingMethod(true);
    AuthenticationService.twoFALogin({
      email: enabledMethods?.email,
      code: otp,
      type,
    })
      .then((res) => {
        const { data } = res.data;
        const { accessToken } = data;
        const decoded: any = jwt_decode(accessToken);
        setLoginData({
          accessToken,
          isUserLoggedIn: true,
          isOrgSelected: false,
          ...decoded,
          role: EMP_ROLES.Admin,
        });
        navigate('/orgs', { replace: true });
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        setVerifyingMethod(false);
      });
  };

  useEffect(() => {
    if (enabledMethods?.methods?.length === 1) {
      setVerifyMethods('email');
    }
    if (enabledMethods?.methods?.length > 1) {
      setVerifyMethods('totp');
    }
  }, [enabledMethods]);

  // Hooks
  useEffect(() => {
    doLogout();
  }, []);

  // UI Elements
  return (
    <Box className="login-form-wrapper">
      {enabledMethods?.methods.length > 0 ? (
        <>
          <Typography data-testid="login-title" className="title" variant="h2">
            Security Verification
          </Typography>
          <Typography className="subtitle" variant="body1">
            To verify your account, please complete the any one of the following
            verification.
          </Typography>

          {!isMethodSelected ? (
            <>
              {enabledMethods.methods.includes('totp') && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  mt={3}
                  onClick={() => setVerifyMethods('totp')}
                >
                  <Radio
                    checked={verifyMethods === 'totp'}
                    sx={{ padding: 0 }}
                  />
                  <Typography sx={{ fontSize: 16 }}>Authenticator</Typography>
                </Box>
              )}

              {enabledMethods.methods.includes('email') && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  mt={6}
                  onClick={() => setVerifyMethods('email')}
                >
                  <Radio
                    checked={verifyMethods === 'email'}
                    sx={{ padding: 0 }}
                  />
                  <Typography sx={{ fontSize: 16 }}>
                    Email - {enabledMethods.email}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                mt={12}
              >
                <Typography
                  sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => setEnabledMethods(null)}
                >
                  Back to Login
                </Typography>
                <AppPrimaryButton
                  title="Proceed"
                  isLoading={submittingMethod}
                  loadingSize={22}
                  loadingText="Proceeding"
                  disabled={submittingMethod}
                  onClick={() => {
                    if (verifyMethods === 'email') {
                      handleSendOTP();
                    }
                    if (verifyMethods === 'totp') {
                      setIsMethodSelected('totp');
                    }
                  }}
                />
              </Box>
            </>
          ) : (
            <>
              <Box mt={5}>
                <OtpInput
                  value={otp}
                  onChange={(value: any) => setOTP(value)}
                  numInputs={6}
                  separator={<span className="px-2" />}
                  isInputNum
                  shouldAutoFocus
                  containerStyle="otp-container"
                  inputStyle="otp-input-box"
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
                mt={12}
              >
                <Typography
                  sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => setIsMethodSelected(null)}
                >
                  Back
                </Typography>
                <AppPrimaryButton
                  title="Verify"
                  isLoading={verifyingMethod}
                  loadingSize={22}
                  loadingText="Verifying"
                  disabled={verifyingMethod}
                  onClick={() => {
                    if (verifyMethods === 'email') {
                      handleTwoFALogin(otp, 'email');
                    }
                    if (verifyMethods === 'totp') {
                      handleTwoFALogin(otp, 'totp');
                    }
                  }}
                />
              </Box>
            </>
          )}
        </>
      ) : (
        // Login Form
        <>
          <Typography data-testid="login-title" className="title" variant="h2">
            Login
          </Typography>
          <Typography className="subtitle" variant="body1">
            Don’t have an account?
            <Typography
              to="https://quixxi.com/clarity-ai/"
              className="subtitle-link"
              component={NavLink}
              variant="body1"
            >
              Contact Admin
            </Typography>
          </Typography>

          <Box pt={10}>
            <Formik
              enableReinitialize
              initialValues={loginForm}
              validationSchema={LoginSchema}
              validateOnMount
              onSubmit={(formValues, formik) => {
                actionOnFormSubmit(formValues, formik);
              }}
            >
              {({ handleSubmit, isSubmitting, isValid }) => (
                <form autoComplete="off">
                  <AppFormikTextField
                    outerLabel
                    label="Email *"
                    name="email"
                    placeholder="Enter email"
                  />

                  <AppFormikPasswordField
                    outerLabel
                    label="Password *"
                    name="password"
                    placeholder="Enter password"
                  />

                  <AppPrimaryButton
                    className="mt-5"
                    fullWidth
                    title="Log In"
                    isLoading={isSubmitting}
                    loadingSize={22}
                    loadingText="LOGGING IN"
                    disabled={isSubmitting || !isValid}
                    onClick={handleSubmit}
                    testid="login-submit"
                    type="submit"
                  />
                  <Box className="justify-center mt-4">
                    <Typography className="forgot-info-text" variant="body1">
                      Did you forget your password?{' '}
                      <Typography
                        to="/forgot-password"
                        className="forgot-subtitle-link ml-1"
                        component={NavLink}
                        variant="body1"
                      >
                        Click here
                      </Typography>
                    </Typography>
                  </Box>

                  <Stack direction="row" className="mt-10">
                    <AzureLoginForm setEnabledMethods={setEnabledMethods} />
                  </Stack>
                </form>
              )}
            </Formik>
          </Box>
        </>
      )}
    </Box>
  );
};

const mapDispatchToProps = (dispatch: any): any => ({
  setLoginData: (data: any) => dispatch(AuthActions.setLoginStatus(data)),
  setDefaultTheme: (data: any) =>
    dispatch(BaseAppActions.setDefaultTheme(data)),
  doLogout: (data: any) => dispatch(AuthActions.doLogout(data)),
});

const mapStateToProps = (state: any): any => ({
  authState: state.authState,
});

export default connect(mapStateToProps, mapDispatchToProps)(LoginForm);
